/** @jsxImportSource ./simboluri-jsx */
// PLANȘA CELOR 5 PUNCTE DE VERIFICARE ALE GRINZII .
//
// Tabelul de verificări spune «M_Rd ≥ M_Ed (R1_jos)» și omul trebuie să știe
// din memorie ce e „R1_jos" și unde stă. Aici punctele se VĂD: elevația
// grinzii, cu reazemele, fețele stâlpilor și zonele critice, iar de pe fețele
// ei pornesc DOUĂ diagrame — efortul |M_Ed| și capacitatea M_Rd, ca în
// charturile foii B «GRINZI CP» (seriile BP/BQ peste aceleași stații:
// capacitatea trebuie s-o îmbrace pe cerere).
//
// Nu inventează nimic: pozițiile sunt `x` din `puncte[grupare][punct]`,
// cifrele sunt E/R/RE ale verificărilor pe care le-a emis Pasul 13, iar zona
// critică e `interfata_stalpi.l_cr_m`. Forma diagramei ÎNTRE stații nu se
// desenează la ramura reazemelor (pasul nu raportează nimic acolo, iar o
// parabolă trasă prin trei puncte ar fi un desen mai sigur pe el decât datele
// — v. desen-3d-doar-ce-e-fizic).
//
// CONVENȚII (direcția A din canvasul de design):
//   · o SINGURĂ scară pentru amândouă ramurile — altfel comparația dintre ele
//     minte (greșeala prinsă chiar în machete);
//   · fiecare punct se desenează pe FIBRA LUI (cele „_sus" în sus, câmpul și
//     cele „_jos" în jos), cu |M_Ed| și M_Rd, nu după semnul momentului: la
//     gruparea fundamentală același moment negativ se verifică și pe
//     capacitatea de jos (R1_jos), iar desenat după semn ar apărea de partea
//     cealaltă a axei și ar părea neacoperit. Semnul rămâne scris lângă linie;
//   · roșu = cererea, albastru = capacitatea, banda dintre ele = rezerva.
//
// Aici stau și piesele direcției C — `MiniPozitie` și `BaraEfortRezistenta` —
// pe care le folosește TABELUL de verificări al paginii: aceeași convenție de
// culoare și aceeași geometrie, într-un rând de tabel.

import { useMemo, useState } from "react";
import { CroireAx, GrindaArmata } from "./tipuri";
import BareLongitudinale from "./BareLongitudinale";
import { VerificareElement, nr } from "./elemente";
import { CotaH, COTA_MARTOR, mFmt } from "./cote";
import { t, tn, useT } from "../i18n";
import type { Cheie } from "../i18n";

const PUNCTE = ["R1_sus", "R1_jos", "camp", "R2_sus", "R2_jos"] as const;
type Punct = (typeof PUNCTE)[number];

const CERERE = "#b5323d";       // --bad: efortul
const CAPACITATE = "#2b5f8a";   // albastrul barelor de jos din secțiuni

const PUNCT_CHEIE: Record<Punct, Cheie> = {
  R1_sus: "struct.pg.punct_r1_sus",
  R1_jos: "struct.pg.punct_r1_jos",
  camp: "struct.pg.punct_camp",
  R2_sus: "struct.pg.punct_r2_sus",
  R2_jos: "struct.pg.punct_r2_jos",
};

const punctText = (p: Punct) => t(PUNCT_CHEIE[p]);

// gruparea vine de la server ca identificator; pe ecran se scrie în limba
// curentă, iar ce nu se cunoaște rămâne cum a venit
const GRUPARE_CHEIE: Record<string, Cheie> = {
  fundamentala: "struct.pg.fundamentala",
  seismica: "struct.pg.seismica",
};
const numeGrupare = (gr: string) =>
  GRUPARE_CHEIE[gr] ? t(GRUPARE_CHEIE[gr]) : gr;

/** a doua și a treia linie din titlul unui punct: gruparea, combinația, verdictul */
const titluGrupare = (grupa: string, combo: string | null) =>
  t("struct.pg.titlu_gruparea", { grupa: numeGrupare(grupa) }) +
  (combo ? t("struct.pg.titlu_combinatia", { combo }) : "");

const GRUPARE_SCURT: Record<string, string> = {
  fundamentala: "F",
  seismica: "S",
};

type PunctDesenat = {
  punct: Punct;
  sus: boolean;
  x: number;            // m, din axul primului stâlp
  grupa: string;
  E: number | null;     // M_Ed [kNm], cu semn
  R: number | null;     // M_Rd [kNm]
  RE: number | null;
  verdict: string;
  combo: string | null;
};

export function tonVerdict(re: number | null, verdict: string): string {
  if (verdict && !verdict.startsWith("SE VERIFICA")) {
    return verdict.startsWith("ATENTIE") ? "var(--amb)" : "var(--bad)";
  }
  if (re != null && isFinite(re) && re < 1.1) return "var(--amb)";
  return "var(--ok)";
}

function tonBg(re: number | null, verdict: string): string {
  if (verdict && !verdict.startsWith("SE VERIFICA")) {
    return verdict.startsWith("ATENTIE") ? "var(--ambbg)" : "var(--badbg)";
  }
  if (re != null && isFinite(re) && re < 1.1) return "var(--ambbg)";
  return "var(--okbg)";
}

// verificările de moment ale grinzii, indexate pe (punct, grupare): de acolo
// vin E, R, R/E și verdictul — exact cifrele din tabelul de deasupra
function indexMomente(verificari: VerificareElement[]) {
  const m = new Map<string, VerificareElement>();
  for (const v of verificari) {
    if (!v.punct || !v.grupa) continue;
    if (!v.nume.startsWith("M_Rd")) continue;
    m.set(`${v.grupa}:${v.punct}`, v);
  }
  return m;
}

// ---------------------------------------------------------------------------
// C — piesele care intră în TABELUL de verificări
// ---------------------------------------------------------------------------

/** Fețele stâlpilor, cu formula pasului (v. armare_grinzi._extrage_eforturi):
 *  la o grindă scurtă între doi stâlpi lați rigidele se ciocnesc și fața se
 *  retrage la jumătatea deschiderii. */
export function feteGrinda(g: GrindaArmata) {
  const L = Number(g.L_ax_m) || 1;
  return {
    L,
    xR1: Math.min(Number(g.off_i_m) || 0, L / 2),
    xR2: Math.max(L - (Number(g.off_j_m) || 0), L / 2),
    lCr: Number(g.interfata_stalpi?.l_cr_m) || 0,
  };
}

/** Grinda în miniatură, cu stația verificării aprinsă pe fibra ei — pentru
 *  celula «Punct» a tabelului. Verificările care nu stau într-un punct, ci pe
 *  o ZONĂ (tăietoarea), primesc zona hașurată; restul, nimic. */
export function MiniPozitie({
  g,
  punct,
  nume,
  culoare = "var(--ok)",
  latime = 82,
}: {
  g: GrindaArmata;
  punct?: string | null;
  nume?: string;
  culoare?: string;
  latime?: number;
}) {
  useT();
  const { L, xR1, xR2, lCr } = feteGrinda(g);
  const W = 84, H = 26;
  const x0 = 4, x1 = 80;
  const px = (x: number) => x0 + (x / L) * (x1 - x0);
  // `punct` vine uneori prefixat cu gruparea („seismica:R2_sus" — verificarea
  // de ductilitate); stația e partea de după două puncte
  const p = String(punct ?? "").split(":").pop() ?? "";
  const stat = (PUNCTE as readonly string[]).includes(p) ? (p as Punct) : null;
  const zona = !stat && /zona critic|zona curent/i.test(nume ?? "")
    ? (/zona critic/i.test(nume ?? "") ? "critica" : "curenta")
    : null;
  if (!stat && !zona) return null;
  const xs = stat
    ? stat.startsWith("R1") ? xR1
      : stat.startsWith("R2") ? xR2
        : (xR1 + xR2) / 2
    : 0;
  const lc = Math.min(lCr, xR2 - xR1);
  const wi = Math.max(px(xR1) - px(0), 3);
  const wj = Math.max(px(L) - px(xR2), 3);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: latime, display: "block" }}>
      <title>
        {stat
          ? `${punctText(stat)} — x = ${mFmt(xs)} m`
          : zona === "critica"
            ? t("struct.pg.mini_zone_critice", { l: mFmt(lCr) })
            : t("struct.pg.mini_zona_curenta")}
      </title>
      <rect x={x0} y={8} width={x1 - x0} height={11} fill="#ececec"
            stroke="#737373" strokeWidth={0.9} />
      <rect x={x0} y={8} width={x1 - x0} height={3.4} fill="#dbdfe4"
            stroke="#8f9298" strokeWidth={0.5} />
      <rect x={px(0) - wi} y={5} width={wi * 2} height={17}
            fill="#ebebeb" stroke="#c5c5c5" strokeWidth={0.7} />
      <rect x={px(L) - wj} y={5} width={wj * 2} height={17}
            fill="#ebebeb" stroke="#c5c5c5" strokeWidth={0.7} />
      {zona === "critica" && lc > 0.01 && (
        <>
          <rect x={px(xR1)} y={8} width={px(xR1 + lc) - px(xR1)} height={11}
                fill="var(--amb)" opacity={0.4} />
          <rect x={px(xR2 - lc)} y={8} width={px(xR2) - px(xR2 - lc)} height={11}
                fill="var(--amb)" opacity={0.4} />
        </>
      )}
      {zona === "curenta" && lc > 0.01 && (
        <rect x={px(xR1 + lc)} y={8}
              width={Math.max(px(xR2 - lc) - px(xR1 + lc), 1)} height={11}
              fill="var(--amb)" opacity={0.4} />
      )}
      {stat && (
        <circle cx={px(xs)} cy={stat.endsWith("_sus") ? 10.6 : 16.6} r={3.1}
                fill="#fff" stroke={culoare} strokeWidth={1.7} />
      )}
    </svg>
  );
}

/** Bara «cât din capacitate e consumat»: lățimea plină = R, umplutura = |E|,
 *  pragul albastru = capacitatea. Merge pe ORICE unitate (kNm, kN, mm, cm²),
 *  fiindcă scara e a RÂNDULUI — comparația între puncte se face în planșă,
 *  care are o scară comună. */
export function BaraEfortRezistenta({
  E,
  R,
  RE,
  verdict = "",
  latime = 88,
}: {
  E: number | string | null | undefined;
  R: number | string | null | undefined;
  RE: number | null;
  verdict?: string;
  latime?: number;
}) {
  const e = typeof E === "number" ? Math.abs(E) : null;
  const r = typeof R === "number" ? Math.abs(R) : null;
  if (e == null || r == null || !(r > 0)) return null;
  const culoare = tonVerdict(RE, verdict);
  const plin = Math.min(e / r, 1);
  const depasit = e > r;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          title={t("struct.pg.bara_consumat", { e: nr(e, 2), r: nr(r, 2), p: Math.round((e / r) * 100) })}>
      <svg viewBox={`0 0 ${latime} 14`}
           style={{ width: latime, flex: "none", display: "block" }}>
        <line x1={1} y1={7} x2={latime - 1} y2={7} stroke="#e5e5e5"
              strokeWidth={6} strokeLinecap="round" />
        <line x1={1} y1={7} x2={Math.max(1 + plin * (latime - 2), 2.5)} y2={7}
              stroke={CERERE} strokeWidth={6} strokeLinecap="round" />
        <line x1={latime - 1} y1={1} x2={latime - 1} y2={13}
              stroke={CAPACITATE} strokeWidth={2.2} />
        {depasit && (
          <path d={`M ${latime - 7},2.5 L ${latime - 2},7 L ${latime - 7},11.5`}
                fill="none" stroke={CERERE} strokeWidth={1.6} />
        )}
      </svg>
      <b className="sg-mono" style={{ fontSize: 12, color: culoare }}>
        {RE != null && isFinite(RE) ? nr(RE, 2) : "—"}
      </b>
    </span>
  );
}

// ---------------------------------------------------------------------------
// A — planșa
// ---------------------------------------------------------------------------

type Eticheta = {
  cx: number; w: number; text: string;
  culoare: string; bg: string; titlu: string;
};

const RAI = 21;         // înălțimea unui rai de plăcuțe

function aseaza(et: Eticheta[], W: number, padX: number): number[] {
  const raiuri: { a: number; b: number }[][] = [];
  const out: number[] = new Array(et.length).fill(0);
  const ordine = et.map((_, i) => i).sort((i, j) => et[i].cx - et[j].cx);
  for (const i of ordine) {
    const w = et[i].w;
    const a = Math.max(padX, Math.min(W - padX - w, et[i].cx - w / 2));
    const b = a + w;
    let k = 0;
    for (; k < raiuri.length; k++) {
      if (!raiuri[k].some((z) => a < z.b + 8 && z.a < b + 8)) break;
    }
    if (k === raiuri.length) raiuri.push([]);
    raiuri[k].push({ a, b });
    out[i] = k;
  }
  return out;
}

/** Cele 5 stații ale unei grinzi, gata de desenat: poziția, fibra, cererea,
 *  capacitatea și verdictul — pe gruparea cerută sau pe cea care guvernează
 *  (R/E minim). Stă la nivel de modul fiindcă o folosesc DOUĂ planșe: cea a
 *  deschiderii și cea a axului întreg. */
export function puncteDesenate(
  g: GrindaArmata,
  idxM: Map<string, VerificareElement>,
  mod: "guvernanta" | "fundamentala" | "seismica"
): PunctDesenat[] {
  const out: PunctDesenat[] = [];
  for (const p of PUNCTE) {
    const cand = (
      mod === "guvernanta" ? ["fundamentala", "seismica"] : [mod]
    ).filter((gr) => g.puncte?.[gr]?.[p]);
    if (!cand.length) continue;
    const gr = cand.reduce((best, c) => {
      const re = idxM.get(`${c}:${p}`)?.RE;
      const reBest = idxM.get(`${best}:${p}`)?.RE;
      if (re == null) return best;
      if (reBest == null) return c;
      return re < reBest ? c : best;
    }, cand[0]);
    const v = idxM.get(`${gr}:${p}`);
    const pe = g.puncte[gr][p];
    out.push({
      punct: p,
      sus: p.endsWith("_sus"),
      x: Number(pe.x ?? 0),
      grupa: gr,
      E: typeof v?.E === "number" ? v.E : pe.M,
      R: typeof v?.R === "number" ? v.R : (g.m_rd?.[gr]?.[p] ?? null),
      RE: v?.RE ?? null,
      verdict: v?.verdict ?? "",
      combo: pe.combo ?? null,
    });
  }
  return out;
}


/** Abscisa LOCALĂ, în deschiderea `g`, a unui punct dat pe abscisa AXULUI;
 *  `null` când punctul nu cade pe deschiderea asta.
 *
 *  Regula e una singură pentru toate planșele deschiderii — elevația și
 *  vederea 3D —: croiala vine pe ax (Pasul 13), iar o grindă parcursă invers
 *  pe ax își citește abscisele de la dreapta la stânga. */
export function peDeschidere(g: GrindaArmata, s: number): number | null {
  const s0 = Number(g.ax?.s0_m) || 0;
  const L = Number(g.L_ax_m) || 0;
  if (s < s0 - 1e-6 || s > s0 + L + 1e-6) return null;
  return g.ax?.inversata ? s0 + L - s : s - s0;
}

/** Petrecerile care cad în deschiderea desenată, aduse în abscisa ei locală. */
export function petreceriPeDeschidere(g: GrindaArmata,
                                     croire?: CroireAx | null) {
  if (!croire) return [];
  const out: {
    x: number; l0: number; sus: boolean; fortata: boolean; zona: string;
    phi: number;
  }[] = [];
  for (const t of croire.trasee) {
    for (const i of t.innadiri) {
      const x = peDeschidere(g, (i.s_m + i.s2_m) / 2);
      if (x == null) continue;
      out.push({
        x,
        l0: i.l0_m,
        sus: t.rol.includes("sus"),
        fortata: i.fortata,
        zona: i.zona,
        phi: t.phi_mm,
      });
    }
  }
  return out;
}


export default function PuncteGrinda({
  g,
  verificari,
  ax,
  croire,
}: {
  g: GrindaArmata;
  verificari: VerificareElement[];
  /** celelalte deschideri ale AXULUI coliniar, în ordinea de pe ax (inclusiv
   *  cea curentă) — când lipsesc, planșa arată doar deschiderea */
  ax?: GrindaPeAx[];
  /** croiala barelor pe ax: din ea se desenează petrecerile */
  croire?: CroireAx | null;
}) {
  useT();
  // ce se arată: gruparea care GUVERNEAZĂ fiecare punct (R/E minim) sau una
  // dintre cele două, la cerere
  const [mod, setMod] = useState<"guvernanta" | "fundamentala" | "seismica">(
    "guvernanta"
  );
  // deschiderea curentă sau tot axul (numai când axul are ce arăta)
  const areAx = !!(ax && ax.length > 1 && g.ax);
  const [intindere, setIntindere] = useState<"grinda" | "ax">("grinda");
  const peAx = areAx && intindere === "ax";
  const idxM = useMemo(() => indexMomente(verificari), [verificari]);

  const puncte: PunctDesenat[] = useMemo(
    () => puncteDesenate(g, idxM, mod), [g, idxM, mod]);

  // ce PICĂ în afara celor 5 puncte (tăietoarea pe zone, SLS, constructivele):
  // altfel planșa arată cinci markere verzi pe un element roșu
  const inAfara = verificari.filter(
    (v) =>
      !v.verdict.startsWith("SE VERIFICA") &&
      !(v.punct && (PUNCTE as readonly string[]).includes(v.punct))
  );

  if (!puncte.length) {
    return (
      <div className="sg-muted" style={{ fontSize: 12 }}>
        {t("struct.pg.rezultatul_grinzii_poart")}
      </div>
    );
  }

  const { L, xR1, xR2, lCr } = feteGrinda(g);
  const l0 = Number(g.l0_m) || xR2 - xR1;
  const lCrDes = Math.min(lCr, xR2 - xR1);
  const totCritic = lCr > 0.01 && lCr >= xR2 - xR1 - 1e-6;
  const hM = (Number(g.h_cm) || 50) / 100;
  const hPlM = (Number(g.h_pl_cm) || 0) / 100;

  const W = 660;
  const padX = 26;
  // aceeași scală pe ambele axe pentru ELEVAȚIE (proporțiile trebuie să rămână
  // reale), plafonată ca o grindă scurtă să nu iasă un turn
  const sc = Math.min((W - 2 * padX) / L, 120);
  const bw = L * sc;
  const ox = padX + (W - 2 * padX - bw) / 2;
  const px = (x: number) => ox + x * sc;
  const hpx = Math.max(hM * sc, 16);
  const hplpx = Math.min(hPlM * sc, hpx * 0.6);
  const mij = (xR1 + xR2) / 2;

  const sus = puncte.filter((p) => p.sus);
  const jos = puncte.filter((p) => !p.sus);
  // ordonatele: |M_Ed| și M_Rd, o SINGURĂ scară peste ambele ramuri
  const mMax = Math.max(
    1,
    ...puncte.flatMap((z) => [Math.abs(z.E ?? 0), Math.abs(z.R ?? 0)])
  );
  const SEMI = 112;
  const kM = SEMI / mMax;

  const placuta = (p: PunctDesenat): Eticheta => {
    const text =
      `${p.punct} · R/E ${p.RE != null ? nr(p.RE, 2) : "—"}` +
      (mod === "guvernanta" ? ` (${GRUPARE_SCURT[p.grupa] ?? p.grupa})` : "");
    return {
      cx: px(p.x),
      w: text.length * 5.35 + 16,
      text,
      culoare: tonVerdict(p.RE, p.verdict),
      bg: tonBg(p.RE, p.verdict),
      titlu:
        `${punctText(p.punct)}\nM_Ed ${nr(p.E ?? 0, 1)} kNm, ` +
        `M_Rd ${nr(p.R ?? 0, 1)} kNm, x = ${mFmt(p.x)} m\n` +
        titluGrupare(p.grupa, p.combo) +
        (p.verdict ? `\n${p.verdict}` : ""),
    };
  };
  const plSus = sus.map(placuta);
  const plJos = jos.map(placuta);
  const raiSus = aseaza(plSus, W, padX);
  const raiJos = aseaza(plJos, W, padX);
  const nSus = Math.max(...raiSus, 0) + 1;
  const nJos = Math.max(...raiJos, 0) + 1;

  // vertical: plăcuțe sus · ramura de sus · elevația · ramura de jos ·
  // plăcuțe jos · cotele
  const yPlSus = 6;
  const yTop = yPlSus + nSus * RAI + 18 + SEMI;   // fața de sus a grinzii
  const yBot = yTop + hpx;                        // fața de jos
  const yPlJos = yBot + SEMI + 22;
  const yCota = yPlJos + nJos * RAI + 14;
  const yCotaL = yCota + 24;
  const H = yCotaL + 22;

  // ordonata unui moment, pe fibra lui
  const pyM = (v: number, susu: boolean) =>
    susu ? yTop - Math.abs(v) * kM : yBot + Math.abs(v) * kM;

  const placutaSvg = (
    e: Eticheta, rai: number, deasupra: boolean, marker: number
  ) => {
    const x = Math.max(padX, Math.min(W - padX - e.w, e.cx - e.w / 2));
    const y = deasupra ? yPlSus + (nSus - 1 - rai) * RAI : yPlJos + rai * RAI;
    const h = RAI - 4;
    return (
      <g key={`${e.text}-${deasupra}`}>
        <line x1={e.cx} y1={marker} x2={e.cx} y2={deasupra ? y + h : y}
              stroke={COTA_MARTOR} strokeWidth={0.7} strokeDasharray="3 3" />
        <rect x={x} y={y} width={e.w} height={h} rx={4} fill={e.bg} />
        <title>{e.titlu}</title>
        <text x={x + 8} y={y + h - 5} fontFamily="var(--mono)" fontSize={10}
              fontWeight={600} fill={e.culoare}>
          {e.text}
        </text>
      </g>
    );
  };

  // valorile scrise lângă linii: în stânga se ancorează la început, în dreapta
  // la sfârșit (altfel ies din casetă)
  // `sarit` ridică eticheta peste linie: pe ramura de jos câmpul și reazemul 2
  // cad la 40 px unul de altul, iar două etichete de 70 px pe același rai se
  // suprapun («M_Ed 15,M_Ed 16,6» — măsurat pe GR50.5)
  const valoare = (p: PunctDesenat, cap: boolean, sarit = false, flip = false) => {
    const laStanga = flip ? p.x > mij : p.x <= mij;
    const v = cap ? p.R : p.E;
    const dy = p.sus
      ? (sarit ? 12 : -5)
      : (sarit ? -5 : 11);
    const y = pyM(v ?? 0, p.sus) + dy;
    return (
      <text key={`${p.punct}-${cap ? "R" : "E"}`}
            x={px(p.x) + (laStanga ? 11 : -11)} y={y}
            textAnchor={laStanga ? "start" : "end"}
            fontFamily="var(--mono)" fontSize={10}
            fontWeight={500} fill={cap ? CAPACITATE : CERERE}>
        {(cap ? "M_Rd " : "M_Ed ") +
          (v == null ? "—" : (v < 0 ? "−" : "") + nr(Math.abs(v), 1))}
      </text>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span className="ob-lbl">{t("struct.pg.titlu_diagrama")}</span>
        <span className="sg-mono" style={{ fontSize: 11, color: "var(--mut)" }}>
          {g.element} · {nr(Number(g.b_cm))}×{nr(Number(g.h_cm))} · {g.nivel}
          {g.stalp_i && g.stalp_j ? ` · ${g.stalp_i} → ${g.stalp_j}` : ""}
        </span>
      </div>
      <div className="sg-muted" style={{ fontSize: 12, paddingBottom: 8 }}>
        {/* «|M_Ed|» intră ca parametru: bara verticală e separatorul de plural al catalogului */}
        {t(areAx ? "struct.pg.explicatie_ax" : "struct.pg.explicatie", { m: "|M_Ed|" })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingBottom: 8 }}>
        {areAx && (
          <div className="pf-tabs" style={{ marginBottom: 0 }}>
            <button className={`pf-tab ${intindere === "grinda" ? "active" : ""}`}
                    onClick={() => setIntindere("grinda")}
                    title={t("struct.pg.numai_deschiderea_cotele")}>
              {t("struct.pg.tab_deschiderea")}
            </button>
            <button className={`pf-tab ${intindere === "ax" ? "active" : ""}`}
                    onClick={() => setIntindere("ax")}
                    title={tn("struct.pg.tab_ax_titlu#n", Number(g.ax?.n_grinzi) || 0)}>
              {tn("struct.pg.tab_ax#n", Number(g.ax?.n_grinzi) || 0)}
            </button>
          </div>
        )}
        <span style={{ flex: 1 }} />
        <div className="pf-tabs" style={{ marginBottom: 0 }}>
          <button className={`pf-tab ${mod === "guvernanta" ? "active" : ""}`}
                  onClick={() => setMod("guvernanta")}
                  title={t("struct.pg.punct_gruparea_r")}>
            {t("struct.pg.gruparea_decide")}
          </button>
          <button className={`pf-tab ${mod === "fundamentala" ? "active" : ""}`}
                  onClick={() => setMod("fundamentala")}
                  title={t("struct.pg.1_35g_1")}>
            {t("struct.pg.fundamentala")}
          </button>
          <button className={`pf-tab ${mod === "seismica" ? "active" : ""}`}
                  onClick={() => setMod("seismica")}
                  title={t("struct.pg.g_q_seism")}>
            {t("struct.pg.seismica")}
          </button>
        </div>
      </div>

      {peAx && ax ? (
        <DiagramaAx grinzi={ax} curenta={g.element} mod={mod}
                    croire={croire} />
      ) : (
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`}
             style={{ width: "100%", minWidth: 460, background: "#fff",
                      border: "1px solid var(--line)", borderRadius: 6 }}>
          {/* ---- ramurile de moment: rezerva, capacitatea, cererea ------- */}
          {[{ r: sus, s: true }, { r: jos, s: false }].map(({ r, s }, k) => {
            if (!r.length) return null;
            // la reazeme capacitatea se știe DOAR pe fața stâlpului: se
            // desenează ca prag pe lungimea zonei critice. Pe fibra de jos
            // toate trei stațiile sunt raportate, deci se leagă.
            const pragA = (p: PunctDesenat) =>
              p.x <= mij ? px(p.x) : px(Math.max(p.x - lCrDes, xR1));
            const pragB = (p: PunctDesenat) =>
              p.x <= mij ? px(Math.min(p.x + lCrDes, xR2)) : px(p.x);
            return (
              <g key={k}>
                {s ? (
                  r.map((p, i) => (
                    <g key={i}>
                      <path
                        d={`M ${pragA(p)},${pyM(p.E ?? 0, true)} L ${pragB(p)},${pyM(p.E ?? 0, true)} ` +
                           `L ${pragB(p)},${pyM(p.R ?? 0, true)} L ${pragA(p)},${pyM(p.R ?? 0, true)} Z`}
                        fill={CAPACITATE} opacity={0.07} />
                      <line x1={pragA(p)} y1={pyM(p.R ?? 0, true)}
                            x2={pragB(p)} y2={pyM(p.R ?? 0, true)}
                            stroke={CAPACITATE} strokeWidth={2.4} />
                      <line x1={pragA(p)} y1={pyM(p.E ?? 0, true)}
                            x2={pragB(p)} y2={pyM(p.E ?? 0, true)}
                            stroke={CERERE} strokeWidth={1.6} />
                    </g>
                  ))
                ) : (
                  <>
                    <path
                      d={"M " + r.map((p) => `${px(p.x)},${pyM(p.E ?? 0, false)}`).join(" L ") +
                         " L " + r.slice().reverse().map((p) => `${px(p.x)},${pyM(p.R ?? 0, false)}`).join(" L ") + " Z"}
                      fill={CAPACITATE} opacity={0.07} />
                    <polyline
                      points={r.map((p) => `${px(p.x)},${pyM(p.R ?? 0, false)}`).join(" ")}
                      fill="none" stroke={CAPACITATE} strokeWidth={2.2} />
                    <polyline
                      points={r.map((p) => `${px(p.x)},${pyM(p.E ?? 0, false)}`).join(" ")}
                      fill="none" stroke={CERERE} strokeWidth={1.6} />
                    {r.map((p, i) => (
                      <line key={i} x1={px(p.x) - 9} y1={pyM(p.R ?? 0, false)}
                            x2={px(p.x) + 9} y2={pyM(p.R ?? 0, false)}
                            stroke={CAPACITATE} strokeWidth={2.6} />
                    ))}
                  </>
                )}
                {/* ordonata cererii + markerul, ca într-o diagramă de momente */}
                {r.map((p, i) => (
                  <g key={`o${i}`}>
                    <line x1={px(p.x)} y1={s ? yTop : yBot}
                          x2={px(p.x)} y2={pyM(p.E ?? 0, s)}
                          stroke={CERERE} strokeWidth={1} />
                    <circle cx={px(p.x)} cy={pyM(p.E ?? 0, s)} r={3}
                            fill={CERERE} />
                  </g>
                ))}
                {/* când cererea și capacitatea cad la mai puțin de 22 px una
                    de alta (grindă la limită), etichetele lor nu mai încap pe
                    aceeași parte: cea a capacității trece de partea cealaltă */}
                {r.map((p) =>
                  valoare(
                    p, true, false,
                    Math.abs(pyM(p.E ?? 0, p.sus) - pyM(p.R ?? 0, p.sus)) < 22
                  )
                )}
                {/* etichetele cererii se alternează peste/sub linie când
                    stațiile sunt prea aproape una de alta */}
                {(() => {
                  const sarite: boolean[] = [];
                  r.forEach((p, i) => {
                    const vecin = r[i - 1];
                    // când cererea DEPĂȘEȘTE capacitatea, eticheta ei cade
                    // exact peste cea a capacității (măsurat pe o grindă
                    // picată la câmp) — se mută de partea cealaltă a liniei
                    const trece = Math.abs(p.E ?? 0) > Math.abs(p.R ?? 0);
                    sarite.push(
                      trece ||
                      (!!vecin &&
                        Math.abs(px(p.x) - px(vecin.x)) < 96 &&
                        !sarite[i - 1])
                    );
                  });
                  return r.map((p, i) => valoare(p, false, sarite[i]));
                })()}
              </g>
            );
          })}

          {/* ---- ELEVAȚIA ------------------------------------------------ */}
          {[
            { x: 0, off: Number(g.off_i_m) || 0 },
            { x: L, off: Number(g.off_j_m) || 0 },
          ].map((s, i) => {
            const w = Math.max(2 * s.off * sc, 7);
            return (
              <rect key={i} x={px(s.x) - w / 2} y={yTop - 5} width={w}
                    height={hpx + 10} fill="#ebebeb" stroke="#c5c5c5"
                    strokeWidth={0.8} />
            );
          })}
          <rect x={px(0)} y={yTop} width={bw} height={hpx}
                fill="#ececec" stroke="#595959" strokeWidth={1.4} />
          {hplpx > 1 && (
            <rect x={px(0)} y={yTop} width={bw} height={hplpx}
                  fill="#dbdfe4" stroke="#8f9298" strokeWidth={0.8} />
          )}
          {lCrDes > 0.01 &&
            [xR1, xR2 - lCrDes].map((x0, i) => (
              <rect key={i} x={px(x0)} y={yTop} width={lCrDes * sc}
                    height={hpx} fill="var(--amb)" opacity={0.16} />
            ))}
          {[xR1, xR2].map((x, i) => (
            <line key={i} x1={px(x)} y1={yTop - 8} x2={px(x)} y2={yBot + 8}
                  stroke={CERERE} strokeWidth={0.9} strokeDasharray="4 3"
                  opacity={0.8} />
          ))}

          {/* ---- PETRECERILE: paranteza cu punct, pe fibra barei ---------
              Marcajul nu e materie (aia se vede în elevația 3D), e o
              ABSCISĂ: paranteză peste lungimea l₀, cu punctul în mijloc, în
              culoarea fibrei. Cine se uită vede că petrecerea cade acolo
              unde ramura ei e cea mai joasă — adică unde bara lucrează cel
              mai puțin (EC2 §8.7.2(1)). */}
          {petreceriPeDeschidere(g, croire).map((p, i) => {
            const a = px(Math.max(p.x - p.l0 / 2, 0));
            const b = px(Math.min(p.x + p.l0 / 2, L));
            const y = p.sus ? yTop - 7 : yBot + 7;
            const col = p.fortata ? "var(--amb)" : (p.sus ? CERERE : CAPACITATE);
            const dy = p.sus ? -6 : 6;
            return (
              <g key={`pet-${i}`}>
                <title>
                  {t(p.sus ? "struct.pg.petrecere_sus" : "struct.pg.petrecere_jos", {
                    phi: p.phi, l0: mFmt(p.l0),
                    zona: p.zona || t("struct.pg.fara_zona_libera"),
                  })}
                </title>
                <path d={`M${a} ${y + dy} V${y} H${b} V${y + dy}`}
                      fill="none" stroke={col} strokeWidth={1.8} />
                <circle cx={(a + b) / 2} cy={y} r={2.2} fill={col} />
              </g>
            );
          })}

          {plSus.map((e, i) => placutaSvg(e, raiSus[i], true, pyM(sus[i].R ?? 0, true)))}
          {plJos.map((e, i) => placutaSvg(e, raiJos[i], false, pyM(jos[i].R ?? 0, false)))}

          {/* ---- COTELE -------------------------------------------------- */}
          <g stroke={COTA_MARTOR} strokeWidth={0.6} strokeDasharray="3 3">
            <line x1={px(0)} y1={yPlJos + nJos * RAI} x2={px(0)} y2={yCota - 2} />
            <line x1={px(L)} y1={yPlJos + nJos * RAI} x2={px(L)} y2={yCota - 2} />
          </g>
          {xR1 > 0.01 && (
            <CotaH x1={px(0)} x2={px(xR1)} y={yCota} text={mFmt(xR1)} size={9}
                   deasupra />
          )}
          <CotaH x1={px(xR1)} x2={px(xR2)} y={yCota} text={`l₀ = ${mFmt(l0)} m`}
                 size={(xR2 - xR1) * sc < 92 ? 8.5 : 10} deasupra />
          {L - xR2 > 0.01 && (
            <CotaH x1={px(xR2)} x2={px(L)} y={yCota} text={mFmt(L - xR2)} size={9}
                   deasupra />
          )}
          <CotaH x1={px(0)} x2={px(L)} y={yCotaL}
                 text={`L_ax = ${mFmt(L)} m`} />
        </svg>
        {/* SUB diagrama de acoperire, pe ACELEAȘI abscise: din ce bare e
            capabilul de deasupra. Croiala e în abscisa AXULUI, deci se aduce
            în cea a deschiderii; ce trece dincolo de reazem se taie de
            casetă — și tocmai asta se vede, că bara CONTINUĂ. */}
        {croire && (
          <BareLongitudinale
            croire={croire} W={W} Ltot={L}
            px={(sAx) => {
              const s0 = Number(g.ax?.s0_m) || 0;
              const xl = g.ax?.inversata ? s0 + L - sAx : sAx - s0;
              return px(xl);
            }}
            evidentiaza={null} />
        )}
      </div>
      )}

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8,
                    fontSize: 11, color: "var(--mut)", alignItems: "center" }}>
        <span style={{ color: CERERE }}>{t("struct.pg.leg_efort", { m: "|M_Ed|" })}</span>
        <span style={{ color: CAPACITATE }}>{t("struct.pg.leg_rezistenta")}</span>
        <span>{t("struct.pg.leg_banda")}</span>
        <span>
          {totCritic
            ? t("struct.pg.leg_zona_toata", { l: mFmt(lCr) })
            : t("struct.pg.leg_zona_fete", { l: mFmt(lCr) })}
        </span>
        <span style={{ color: CERERE }}>{t("struct.pg.fata_stalpului_aici")}</span>
        {mod === "guvernanta" && <span>{t("struct.pg.f_fundamentala_s")}</span>}
      </div>
      {inAfara.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8,
                      alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "var(--mut)" }}>
            {t("struct.pg.trec_dar_intr")}
          </span>
          {inAfara.map((v, i) => (
            <span key={i}
                  className={`ob-chip ${v.verdict.startsWith("ATENTIE") ? "ob-c-amb" : "ob-c-bad"}`}
                  style={{ fontSize: 10 }}
                  title={`${v.verdict}${v.punct ? ` — ${v.punct}` : ""}`}>
              {v.nume}{v.RE != null ? ` · R/E ${nr(v.RE, 2)}` : ""}
            </span>
          ))}
        </div>
      )}
      <div className="sg-muted" style={{ fontSize: 11, marginTop: 6 }}>
        {t("struct.pg.punct_deseneaza_fibra")}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// PLANȘA AXULUI — aceeași diagramă, pe toate deschiderile
// coliniare
// ---------------------------------------------------------------------------
//
// O grindă nu se citește singură: momentul de pe fața ei de reazem e produs de
// deschiderea de alături, iar barele de sus trec prin nod și sunt ACEEAȘI bară
// pe tot axul (v. `armare_grinzi.axe_coliniare`). Planșa asta pune deschiderile
// cap la cap, pe o SINGURĂ scară de lungime și o SINGURĂ scară de moment, și
// evidențiază porțiunea grinzii curente.
//
// Ce se desenează pe deschiderea curentă e identic cu planșa de o deschidere
// (aceleași 5 stații, aceleași culori, aceleași etichete). Pe celelalte rămân
// doar markerele și pragurile: 15 plăcuțe pe un ax de trei deschideri nu se
// mai pot citi, iar cifrele lor se văd oricum la hover.

export type GrindaPeAx = {
  g: GrindaArmata;
  verificari: VerificareElement[];
};

function DiagramaAx({
  grinzi,
  curenta,
  mod,
  croire,
}: {
  grinzi: GrindaPeAx[];
  curenta: string;
  mod: "guvernanta" | "fundamentala" | "seismica";
  /** croiala barelor pe ax: din ea se desenează planșa barelor de dedesubt */
  croire?: CroireAx | null;
}) {
  const sirag = useMemo(() => {
    const lst = grinzi
      .filter((z) => z.g?.ax)
      .slice()
      .sort((a, b) => (a.g.ax!.indice ?? 0) - (b.g.ax!.indice ?? 0));
    return lst.map((z) => {
      const idxM = indexMomente(z.verificari);
      const L = Number(z.g.L_ax_m) || 1;
      const s0 = Number(z.g.ax!.s0_m) || 0;
      const inv = !!z.g.ax!.inversata;
      const s = (x: number) => s0 + (inv ? L - x : x);
      return {
        ...z,
        L,
        s0,
        s,
        fete: feteGrinda(z.g),
        puncte: puncteDesenate(z.g, idxM, mod),
      };
    });
  }, [grinzi, mod]);

  if (sirag.length < 2) return null;

  const Ltot = Number(sirag[0].g.ax!.L_ax_m)
    || sirag.reduce((a, z) => a + z.L, 0);
  const W = 900;
  const padX = 30;
  const sc = (W - 2 * padX) / Math.max(Ltot, 0.1);
  const ps = (v: number) => padX + v * sc;

  // o SINGURĂ scară de moment peste tot axul — altfel deschiderile nu se pot
  // compara între ele, adică exact ce vine omul să vadă aici
  const mMax = Math.max(
    1,
    ...sirag.flatMap((z) =>
      z.puncte.flatMap((p) => [Math.abs(p.E ?? 0), Math.abs(p.R ?? 0)]))
  );
  const SEMI = 104;
  const kM = SEMI / mMax;

  const hMax = Math.max(...sirag.map((z) => Number(z.g.h_cm) || 50)) / 100;
  const hpx = Math.max(Math.min(hMax * sc, 42), 16);

  const RAI_AX = 17;
  const nRai = 2;                       // plăcuțele grinzii curente
  const yTitlu = 12;                    // banda „deschiderea din pagină"
  const yPlSus = yTitlu + 8;
  const yTop = yPlSus + nRai * RAI_AX + 16 + SEMI;
  const yBot = yTop + hpx;
  const yPlJos = yBot + SEMI + 20;
  const yCota = yPlJos + nRai * RAI_AX + 14;
  const yCotaL = yCota + 24;
  const H = yCotaL + 22;

  const pyM = (v: number, sus: boolean) =>
    sus ? yTop - Math.abs(v) * kM : yBot + Math.abs(v) * kM;

  const acum = sirag.find((z) => z.g.element === curenta) ?? sirag[0];

  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`}
           style={{ width: "100%", minWidth: 640, background: "#fff",
                    border: "1px solid var(--line)", borderRadius: 6 }}>
        {/* porțiunea grinzii CURENTE, pe toată înălțimea planșei. min/max, nu
            x + lățime: pe un ax parcurs invers `s(0)` e capătul DIN DREAPTA */}
        <rect x={Math.min(ps(acum.s(0)), ps(acum.s(acum.L)))} y={2}
              width={Math.abs(ps(acum.s(acum.L)) - ps(acum.s(0)))}
              height={H - 4} fill="#f4efe2" opacity={0.85} />
        <text x={(ps(acum.s(0)) + ps(acum.s(acum.L))) / 2} y={yTitlu}
              textAnchor="middle" fontFamily="var(--mono)" fontSize={10}
              fontWeight={700} fill="#8a7b52">
          {t("struct.pg.ax_deschiderea_pagina", { element: acum.g.element })}
        </text>

        {sirag.map((z, k) => {
          const estAcum = z.g.element === acum.g.element;
          const { xR1, xR2, lCr } = z.fete;
          const lCrDes = Math.min(lCr, xR2 - xR1);
          const hz = Math.max(Math.min((Number(z.g.h_cm) || 50) / 100 * sc, 42), 16);
          const sus = z.puncte.filter((p) => p.sus);
          const jos = z.puncte.filter((p) => !p.sus);
          const mij = (xR1 + xR2) / 2;
          const pragA = (p: PunctDesenat) =>
            ps(z.s(p.x <= mij ? p.x : Math.max(p.x - lCrDes, xR1)));
          const pragB = (p: PunctDesenat) =>
            ps(z.s(p.x <= mij ? Math.min(p.x + lCrDes, xR2) : p.x));
          const a = Math.min(ps(z.s(0)), ps(z.s(z.L)));
          const b = Math.max(ps(z.s(0)), ps(z.s(z.L)));
          return (
            <g key={k} opacity={estAcum ? 1 : 0.62}>
              {/* elevația deschiderii */}
              <rect x={a} y={yTop} width={b - a} height={hz}
                    fill="#ececec" stroke="#595959"
                    strokeWidth={estAcum ? 1.4 : 0.9} />
              {lCrDes > 0.01 && [xR1, xR2 - lCrDes].map((x0, i) => (
                <rect key={i}
                      x={Math.min(ps(z.s(x0)), ps(z.s(x0 + lCrDes)))}
                      y={yTop} width={lCrDes * sc} height={hz}
                      fill="var(--amb)" opacity={0.16} />
              ))}
              {[xR1, xR2].map((x, i) => (
                <line key={i} x1={ps(z.s(x))} y1={yTop - 7}
                      x2={ps(z.s(x))} y2={yTop + hz + 7}
                      stroke={CERERE} strokeWidth={0.8} strokeDasharray="4 3"
                      opacity={0.75} />
              ))}
              {/* ramura de SUS: capacitatea se știe doar pe zona critică */}
              {sus.map((p, i) => (
                <g key={`s${i}`}>
                  <path
                    d={`M ${pragA(p)},${pyM(p.E ?? 0, true)} L ${pragB(p)},${pyM(p.E ?? 0, true)} ` +
                       `L ${pragB(p)},${pyM(p.R ?? 0, true)} L ${pragA(p)},${pyM(p.R ?? 0, true)} Z`}
                    fill={CAPACITATE} opacity={0.07} />
                  <line x1={pragA(p)} y1={pyM(p.R ?? 0, true)}
                        x2={pragB(p)} y2={pyM(p.R ?? 0, true)}
                        stroke={CAPACITATE} strokeWidth={2.2} />
                  <line x1={pragA(p)} y1={pyM(p.E ?? 0, true)}
                        x2={pragB(p)} y2={pyM(p.E ?? 0, true)}
                        stroke={CERERE} strokeWidth={1.5} />
                </g>
              ))}
              {/* ramura de JOS: toate trei stațiile sunt raportate, se leagă */}
              {jos.length > 1 && (
                <>
                  <path
                    d={"M " + jos.map((p) => `${ps(z.s(p.x))},${pyM(p.E ?? 0, false)}`).join(" L ") +
                       " L " + jos.slice().reverse().map((p) => `${ps(z.s(p.x))},${pyM(p.R ?? 0, false)}`).join(" L ") + " Z"}
                    fill={CAPACITATE} opacity={0.07} />
                  <polyline
                    points={jos.map((p) => `${ps(z.s(p.x))},${pyM(p.R ?? 0, false)}`).join(" ")}
                    fill="none" stroke={CAPACITATE} strokeWidth={2} />
                  <polyline
                    points={jos.map((p) => `${ps(z.s(p.x))},${pyM(p.E ?? 0, false)}`).join(" ")}
                    fill="none" stroke={CERERE} strokeWidth={1.5} />
                </>
              )}
              {z.puncte.map((p, i) => (
                <g key={`o${i}`}>
                  <line x1={ps(z.s(p.x))} y1={p.sus ? yTop : yTop + hz}
                        x2={ps(z.s(p.x))} y2={pyM(p.E ?? 0, p.sus)}
                        stroke={CERERE} strokeWidth={0.9} />
                  <circle cx={ps(z.s(p.x))} cy={pyM(p.E ?? 0, p.sus)}
                          r={estAcum ? 3 : 2.4}
                          fill={tonVerdict(p.RE, p.verdict)}
                          stroke={CERERE} strokeWidth={1}>
                    <title>
                      {`${z.g.element} · ${p.punct}\n${punctText(p.punct)}\n` +
                       `M_Ed ${nr(p.E ?? 0, 1)} kNm, M_Rd ${nr(p.R ?? 0, 1)} kNm` +
                       `${p.RE != null ? `, R/E ${nr(p.RE, 2)}` : ""}\n` +
                       titluGrupare(p.grupa, p.combo) +
                       `${p.verdict ? `\n${p.verdict}` : ""}`}
                    </title>
                  </circle>
                </g>
              ))}
              {/* eticheta deschiderii, pe elevație */}
              <text x={(a + b) / 2} y={yTop + hz / 2 + 3.5}
                    textAnchor="middle" fontFamily="var(--mono)" fontSize={9.5}
                    fontWeight={estAcum ? 700 : 500} fill="#595959">
                {z.g.element}
              </text>
              <CotaH x1={a} x2={b} y={yCota} text={mFmt(z.L)} size={9}
                     deasupra />
            </g>
          );
        })}

        {/* NODURILE intermediare: acolo trece bara de sus dintr-o deschidere
            în alta — de-aia carcasa continuă e aceeași pe tot axul */}
        {sirag.slice(1).map((z, k) => (
          <line key={k} x1={ps(z.s(0))} y1={yTop - SEMI - 4}
                x2={ps(z.s(0))} y2={yBot + SEMI + 4}
                stroke={COTA_MARTOR} strokeWidth={0.7} strokeDasharray="2 4" />
        ))}

        {/* plăcuțele: doar pe deschiderea din pagină */}
        {acum.puncte.map((p, i) => {
          const text = `${p.punct} · R/E ${p.RE != null ? nr(p.RE, 2) : "—"}` +
            (mod === "guvernanta" ? ` (${GRUPARE_SCURT[p.grupa] ?? p.grupa})` : "");
          const w = text.length * 5.1 + 14;
          const cx = ps(acum.s(p.x));
          const x = Math.max(2, Math.min(W - w - 2, cx - w / 2));
          const rai = i % 2;
          const y = p.sus ? yPlSus + (1 - rai) * RAI_AX
                          : yPlJos + rai * RAI_AX;
          const h = RAI_AX - 4;
          return (
            <g key={`pl${i}`}>
              <line x1={cx} y1={p.sus ? y + h : y} x2={cx}
                    y2={pyM(p.R ?? 0, p.sus)} stroke={COTA_MARTOR}
                    strokeWidth={0.7} strokeDasharray="3 3" />
              <rect x={x} y={y} width={w} height={h} rx={4}
                    fill={tonBg(p.RE, p.verdict)} />
              <text x={x + 7} y={y + h - 4} fontFamily="var(--mono)"
                    fontSize={9.5} fontWeight={600}
                    fill={tonVerdict(p.RE, p.verdict)}>
                {text}
              </text>
            </g>
          );
        })}

        <CotaH x1={ps(0)} x2={ps(Ltot)} y={yCotaL}
               text={tn("struct.pg.ax_cota#n", sirag.length, {
                 directie: sirag[0].g.ax!.directie, L: mFmt(Ltot),
               })} />
      </svg>
      {/* SUB diagrama de acoperire, pe ACELEAȘI abscise: din ce bare e
          capabilul de deasupra și unde se înnădesc ele */}
      {croire && (
        <BareLongitudinale
          croire={croire} px={ps} W={W} Ltot={Ltot}
          evidentiaza={{ s1: Math.min(acum.s(0), acum.s(acum.L)),
                         s2: Math.max(acum.s(0), acum.s(acum.L)) }} />
      )}
    </div>
  );
}
